import { Images, type LucideIcon } from 'lucide-react'
import { albumPhotoUrl } from '@/lib/albums'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'

// The front of an album (or a recipe book): its first few photos laid out together, a big one over smaller ones,
// so you can tell them apart at a glance. With no photos yet it's a plain tint with an icon, never a made-up image.
export function AlbumCover({
  covers,
  index,
  className,
  url = (id) => albumPhotoUrl(id, 'thumb'),
  large,
  icon: Icon = Images,
}: {
  covers: string[]
  index: number
  className?: string
  url?: (id: string) => string
  // A sharper copy for the big photo on top, which spans the whole cover; a thumbnail looks soft there on a
  // high-density screen.
  large?: (id: string) => string
  icon?: LucideIcon
}) {
  const n = Math.min(covers.length, 4)
  return (
    <span className={cn('relative block', className)}>
      <span
        aria-hidden="true"
        className={cn(
          'relative grid aspect-[4/5] gap-[3px] overflow-hidden rounded-[1.75rem] bg-background shadow-paper transition-[translate,box-shadow] duration-300 ease-settle group-hover:translate-y-[-3px] group-hover:shadow-lift',
          n === 2 && 'grid-rows-2',
          n === 3 && 'grid-cols-2 grid-rows-[3fr_2fr]',
          n === 4 && 'grid-cols-3 grid-rows-[3fr_1.3fr]',
        )}
      >
        {n === 0 ? (
          <span className={cn('grid place-items-center', tint(index))}>
            <Icon className="size-9 text-pencil" strokeWidth={1.3} />
          </span>
        ) : (
          covers
            .slice(0, n)
            .map((id, i) => (
              <img
                key={id}
                src={url(id)}
                srcSet={large && i === 0 ? `${url(id)} 480w, ${large(id)} 1200w` : undefined}
                sizes={large && i === 0 ? '(min-width: 1024px) 18rem, (min-width: 640px) 30vw, 45vw' : undefined}
                alt=""
                loading="lazy"
                decoding="async"
                className={cn(
                  'size-full min-h-0 object-cover',
                  tint(index + i),
                  i === 0 && n === 3 && 'col-span-2',
                  i === 0 && n === 4 && 'col-span-3',
                )}
              />
            ))
        )}
      </span>
    </span>
  )
}
